/**
 * This file is the only place where Todistukset should import directly 'vtj-koodisto' package.
 *
 * Then other todistukset packages uses vtj-koodisto indirectly through exported functionalities
 * of this file.
 *
 * Try to limit exposed koodisto types if possible and do not re-export vtj-koodisto types directly.
 */
import { getKoodistoCodesByType } from 'vtj-koodisto/src/vtj/common-functions'
import {
  Koodi,
  KoodistoTyyppiKoodiMap,
} from 'vtj-koodisto/src/vtj/koodisto.type'

/**
 * ISO 639 language code.
 *
 * Extended with two additional codes.
 *
 * - '98' meaning "Selväkielisenä"
 * - '99' meaning "Tuntematon"
 */
export type LanguageKoodistoCode = KoodistoTyyppiKoodiMap['Kieli']

/**
 * ISO 3166-1 numeric code.
 *
 * Extended with five additional codes.
 *
 * Added by Todistukset team.
 *
 * - '910' for Kosovo
 *
 * Included in VTJ.
 *
 * - '991' meaning 'Kansalaisuudeton'
 * - '997' meaning 'Ei selvitetty'
 * - '998' meaning 'Tieto selväkielisenä' if not '997'. Country name comes in field 'Kansalaisuus(S|R|E)' or in 'Valtio(S|R|E)'.
 * - '999' meaning 'Tuntematon'
 *
 * '246' is Finland.
 */
export type CountryKoodistoCode = KoodistoTyyppiKoodiMap['VU_vtjValtio']

export enum VtjCountryCodeAdditions {
  KANSALAISUUDETON = '991',
  EI_SELVITETTY = '997',
  TIETO_SELVAKIELISENA = '998',
  TUNTEMATON = '999',
}

export type ValtioKoodistoEntry = {
  koodistoCode: CountryKoodistoCode
  isoCode: string
  fi: string
  sv: string
  en: string
}

export const getCountriesShortNames = (
  includeFormerCountries: boolean
): Array<ValtioKoodistoEntry> => {
  return getKoodistoCodesByType('VU_vtjValtio')
    .filter(
      (country) =>
        country.NIMITEKSTI_TYYPPI === 'lyhenne' &&
        (includeFormerCountries || country.LOPPUPVM === null)
    )
    .map((country) => {
      return {
        koodistoCode: country.KOODI_KOODI,
        isoCode: country.PARI_KOODI_KOODI || '',
        fi: country.NIMITEKSTI_FIN || '',
        sv: country.NIMITEKSTI_SWE || '',
        en: country.NIMITEKSTI_ENG || '',
      }
    })
}

export const isCountryKoodistoCode = (
  possibleCode: unknown
): possibleCode is CountryKoodistoCode => {
  return typeof possibleCode === 'string' && !!findCountryByCode(possibleCode)
}

export const getCountryCodeByShortName = (
  shortName: string | null
): CountryKoodistoCode | null => {
  if (shortName === null) {
    return null
  }

  const code = getKoodistoCodesByType('VU_vtjValtio')
    .filter((country) => country.NIMITEKSTI_TYYPPI === 'lyhenne')
    .find(
      (country) =>
        country.NIMITEKSTI_ENG?.toUpperCase() === shortName.toUpperCase() ||
        country.NIMITEKSTI_FIN?.toUpperCase() === shortName.toUpperCase() ||
        country.NIMITEKSTI_SWE?.toUpperCase() === shortName.toUpperCase()
    )
  return code?.KOODI_KOODI ?? null
}

function findCountryByCode(code: string): Koodi<'VU_vtjValtio'> | undefined {
  return getKoodistoCodesByType('VU_vtjValtio')
    .filter((country) => country.NIMITEKSTI_TYYPPI === 'lyhenne')
    .find((country) => country.KOODI_KOODI === code)
}

/**
 * This function is used to map country shortname into a ISO 3166-1 numeric user assigned code.
 *
 * Function MUST be used only when VTJ-Kysely returs Kansalaisuus3 or Valtio3 code '998'
 * that is our VtjCountryCodeAdditions.TIETO_SELVAKIELISENA.
 *
 * On those cases country name is written as plaintext in Finnish, Swedish or English
 * country name fields in VTJ.
 *
 * We try to map this "Known Unknown" as a user assigned ISO code that we have
 * added into here 'packages/vtj-koodisto/src/vtj/koodisto-manual-additions.js'
 */
export const getUserAssignedCountryCodeByShortNames = (
  shortNameFIN: string | null,
  shortNameSWE: string | null,
  shortNameENG: string | null
): CountryKoodistoCode => {
  const countryCodeByShortName =
    getCountryCodeByShortName(shortNameFIN) ??
    getCountryCodeByShortName(shortNameSWE) ??
    getCountryCodeByShortName(shortNameENG) ??
    VtjCountryCodeAdditions.TIETO_SELVAKIELISENA

  return countryCodeByShortName
}

type NameToLanguageCodeMap = { [k: string]: LanguageKoodistoCode | undefined }

export const getFinnishNameToTwoLetterLanguageCodeMap =
  (): NameToLanguageCodeMap => {
    return getKoodistoCodesByType('Kieli').reduce<NameToLanguageCodeMap>(
      (acc, cur) => {
        // split + trim because we have finnish names such as "viro, eesti"
        cur.NIMITEKSTI_FIN?.split(',').forEach((finnishName) => {
          acc[finnishName.trim()] = cur.KOODI_KOODI
        })
        return acc
      },
      {}
    )
  }
